const COMPLETE_STATUSES = new Set(['complete', 'completed']);
const ACTIVE_STATUSES = new Set(['ongoing', 'in_progress', 'started']);
const MISSED_STATUSES = new Set(['missed', 'expired']);
const NEUTRAL_STATUSES = new Set(['rest', 'replaced', 'cancelled', 'canceled', 'skipped', 'stopped']);

/**
 * Normalize the authorized activity lifecycle once for selectors, peer rows,
 * filters, and section styling. Travel replacement references are projected on
 * the displaced activity, so same-day replacements count as done while delayed
 * replacements remain an explicit offset.
 */
export function getDojoActivityStatus(activity, now = new Date()) {
  if (!activity) {
    return { status: 'unavailable', label: 'Unknown', symbol: '?', completedForDay: false, countable: false, clickedActivity: null };
  }

  const rawStatus = String(activity.status || '').toLowerCase();
  const replacementReference = activity._dojoReplacementReference;
  const sameDayReplacement = Boolean(replacementReference?.activity && replacementReference.relativeDays === 0);
  const complete = COMPLETE_STATUSES.has(rawStatus) || sameDayReplacement;
  const dueTime = activity.due_at ? new Date(activity.due_at).getTime() : null;
  const nowTime = new Date(now).getTime();
  const overdue = !complete && !NEUTRAL_STATUSES.has(rawStatus)
    && Number.isFinite(dueTime) && Number.isFinite(nowTime) && dueTime < nowTime;
  const active = ACTIVE_STATUSES.has(rawStatus) && !overdue;
  const missed = !complete && !active && (MISSED_STATUSES.has(rawStatus) || overdue);
  const rest = rawStatus === 'rest';
  const replaced = !complete && (rawStatus === 'replaced' || Boolean(replacementReference));

  if (complete) return { status: 'done', label: 'Done', symbol: '✓', completedForDay: true, countable: true, clickedActivity: replacementReference?.activity || activity };
  if (active) return { status: 'live', label: 'Live', symbol: '●', completedForDay: false, countable: true, clickedActivity: activity };
  if (missed) return { status: 'missed', label: 'Missed', symbol: '✗', completedForDay: false, countable: true, clickedActivity: activity };
  if (rest) return { status: 'rest', label: 'Rest', symbol: '☾', completedForDay: false, countable: false, clickedActivity: activity };
  if (replaced) return { status: 'replaced', label: 'Offset', symbol: '↔', completedForDay: false, countable: false, clickedActivity: replacementReference?.activity || activity };
  return { status: 'pending', label: 'Pending', symbol: '○', completedForDay: false, countable: true, clickedActivity: activity };
}

export function getDojoSectionStatusClass(status) {
  if (status === 'done') return 'status-complete';
  if (status === 'missed') return 'status-expired';
  if (['live', 'active', 'partial', 'pending'].includes(status)) return 'status-pending';
  return '';
}
